import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useAuth } from "../auth/msalClient";
import { BackendError, openDrawingReference, redeemDrawingShare } from "../api/backendClient";
import { Button } from "@excalidraw-ms/shared-ui";
import FullAccessDialog from "../components/FullAccessDialog";
import UserProfileMenu from "../components/UserProfileMenu";
import { useThemePreference } from "../hooks/useThemePreference";
import {
  captureShareToken,
  clearPendingShareToken,
  persistShareTokenForSignIn,
} from "../utils/shareTokenHandoff";

export default function SharedDrawingPage() {
  const location = useLocation();
  const [shareToken] = useState(() => captureShareToken(location.hash));
  const navigate = useNavigate();
  const {
    account,
    loading,
    signIn,
    signOut,
    getToken,
    hasBackendAccess,
    backendAccessDenied,
    backendCapabilityState: authBackendCapabilityState,
    backendAccessPhase,
    retryBackendAccess,
  } = useAuth();
  const { preference, setPreference } = useThemePreference({
    accountId: account?.homeAccountId,
    getToken,
    syncEnabled: hasBackendAccess,
  });
  const backendCapabilityState =
    authBackendCapabilityState ??
    (hasBackendAccess ? "full" : backendAccessDenied ? "local-only" : "checking");
  const [error, setError] = useState<{ message: string; retryable: boolean } | null>(null);
  const [redemptionAttempt, setRedemptionAttempt] = useState(0);
  const accountId = account?.homeAccountId ?? account?.username;
  const [membershipPromptFor, setMembershipPromptFor] = useState<string>();
  const [showFullAccess, setShowFullAccess] = useState(false);
  const closeFullAccess = useCallback(() => setShowFullAccess(false), []);

  const preserveInvitation = useCallback(() => {
    if (!shareToken) return false;
    try {
      persistShareTokenForSignIn(shareToken);
      return true;
    } catch {
      setShowFullAccess(false);
      console.error("[SharedDrawingPage] Unable to preserve invitation for authentication");
      setError({
        message: "Allow session storage in your browser, then try opening this drawing link again.",
        retryable: true,
      });
      return false;
    }
  }, [shareToken]);

  useEffect(() => {
    // Keep a tab-scoped handoff as well as the URL through a backend-token redirect.
    preserveInvitation();
  }, [preserveInvitation]);

  useEffect(() => {
    if (backendCapabilityState === "full") setShowFullAccess(false);
    if (backendCapabilityState === "local-only" && membershipPromptFor !== accountId) {
      setMembershipPromptFor(accountId);
      setShowFullAccess(true);
    }
  }, [accountId, backendCapabilityState, membershipPromptFor]);

  useEffect(() => {
    if (!accountId || !shareToken || backendCapabilityState !== "full") {
      return;
    }
    if (!preserveInvitation()) return;
    setError(null);
    let cancelled = false;

    void (async () => {
      try {
        const token = await getToken();
        if (cancelled) return;
        const result = shareToken.startsWith("ref:")
          ? await openDrawingReference(token, shareToken.slice(4))
          : await redeemDrawingShare(token, shareToken);
        if (cancelled) return;
        clearPendingShareToken();
        navigate(`/drawing/${encodeURIComponent(result.drawingId)}`, { replace: true });
      } catch (caught) {
        if (cancelled) return;
        console.error("[SharedDrawingPage] Failed to open shared drawing:", caught);
        if (caught instanceof BackendError) {
          if (caught.status === 403) {
            clearPendingShareToken();
            setError({
              message: shareToken.startsWith("ref:")
                ? "You don't have access to this cloud drawing. Ask the owner to share it with you."
                : "You no longer have access to this cloud drawing.",
              retryable: false,
            });
          } else if (caught.status === 404) {
            clearPendingShareToken();
            setError({
              message:
                caught.backendMessage === "Shared drawing no longer exists"
                  ? "This cloud drawing was deleted."
                  : shareToken.startsWith("ref:")
                    ? "This drawing link is invalid or the drawing is unavailable."
                    : "This sharing link is invalid or has expired.",
              retryable: false,
            });
          } else if (caught.status >= 500) {
            persistShareTokenForSignIn(shareToken);
            setError({
              message: "Cloud drawing services are temporarily unavailable. Please try again.",
              retryable: true,
            });
          } else {
            clearPendingShareToken();
            setError({ message: "This drawing link could not be opened.", retryable: false });
          }
        } else {
          persistShareTokenForSignIn(shareToken);
          setError({
            message: "Cloud drawing services are temporarily unavailable. Please try again.",
            retryable: true,
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    accountId,
    backendCapabilityState,
    getToken,
    navigate,
    preserveInvitation,
    redemptionAttempt,
    shareToken,
  ]);

  const retry = () => {
    if (!preserveInvitation()) return;
    setError(null);
    if (backendCapabilityState === "full") {
      setRedemptionAttempt((current) => current + 1);
    } else {
      retryBackendAccess();
    }
  };

  if (loading) return <CenteredMessage>Signing in…</CenteredMessage>;

  if (!shareToken) {
    return <CenteredMessage>This sharing link is invalid or incomplete.</CenteredMessage>;
  }

  if (!account) {
    return (
      <CenteredMessage>
        <div style={{ textAlign: "center" }}>
          <h1 style={{ fontSize: 28, fontWeight: 600, marginBottom: 8 }}>Open shared drawing</h1>
          <p style={{ color: "var(--color-text-secondary)", marginBottom: 24 }}>
            Sign in with your MSIT account to open this drawing.
          </p>
          <Button
            variant="primary"
            onClick={() => {
              if (preserveInvitation()) void signIn();
            }}
          >
            Sign in with Microsoft
          </Button>
          {error && <p role="alert">{error.message}</p>}
        </div>
      </CenteredMessage>
    );
  }

  const busy =
    !error && (backendCapabilityState === "checking" || backendCapabilityState === "full");
  const message =
    error?.message ??
    (backendCapabilityState === "checking"
      ? backendAccessPhase === "preparing-cloud"
        ? "Preparing cloud features…"
        : "Checking Excalidraw-MS access…"
      : backendCapabilityState === "local-only"
        ? "Get full access to open this shared drawing."
        : backendCapabilityState === "temporarily-unavailable"
          ? "Cloud features are temporarily unavailable. Please try again."
          : "Opening shared drawing…");

  return (
    <CenteredMessage>
      <div style={{ position: "absolute", top: 16, right: 16 }}>
        <UserProfileMenu
          account={account}
          themePreference={preference}
          onThemePreferenceChange={setPreference}
          onSignOut={() => {
            if (error?.retryable === false) {
              void signOut();
            } else if (preserveInvitation()) {
              void signOut();
            }
          }}
        />
      </div>
      <div style={{ textAlign: "center", padding: 24 }}>
        <h1 style={{ fontSize: 28, fontWeight: 600 }}>Open shared drawing</h1>
        {(!showFullAccess || backendCapabilityState === "full" || error) && (
          <>
            <p
              role={
                error || backendCapabilityState === "temporarily-unavailable" ? "alert" : "status"
              }
            >
              {message}
            </p>
            {backendCapabilityState === "local-only" && !error ? (
              <Button data-full-access-trigger onClick={() => setShowFullAccess(true)}>
                Get full access
              </Button>
            ) : !busy && error?.retryable !== false ? (
              <Button onClick={retry}>Try again</Button>
            ) : null}
          </>
        )}
      </div>
      {showFullAccess && !error && backendCapabilityState !== "full" && (
        <FullAccessDialog
          key={accountId}
          state={backendCapabilityState}
          phase={backendAccessPhase}
          onRetry={retry}
          onClose={closeFullAccess}
        />
      )}
    </CenteredMessage>
  );
}

function CenteredMessage({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "system-ui, sans-serif",
      }}
    >
      {children}
    </div>
  );
}
